/**
 * UserWay Dynamic Loader
 *
 * A modular utility for dynamically loading UserWay accessibility widget
 * only when needed, ensuring proper initialization before usage.
 */

class UserWayLoader {
  constructor() {
    this.isLoading = false;
    this.isLoaded = false;
    this.loadPromise = null;
    this.accountId = 'jl8vfVix8t'; // UserWay account ID
    this.scriptUrl = 'https://cdn.userway.org/widget.js';
  }

  /**
   * Load UserWay script dynamically
   * @returns {Promise<void>} Promise that resolves when UserWay is loaded and initialized
   */
  load() {
    // Return existing promise if already loading
    if (this.loadPromise) {
      return this.loadPromise;
    }

    // Return resolved promise if already loaded
    if (this.isLoaded && window.UserWay) {
      return Promise.resolve();
    }

    this.isLoading = true;

    this.loadPromise = new Promise((resolve, reject) => {
      try {
        // Check if UserWay is already available (in case script was loaded elsewhere)
        if (window.UserWay && typeof window.UserWay.widgetOpen === 'function') {
          this.isLoaded = true;
          this.isLoading = false;
          resolve();
          return;
        }

        // Create script element
        const script = document.createElement('script');
        script.src = this.scriptUrl;
        script.defer = true;
        script.setAttribute('data-account', this.accountId);

        // Handle successful load
        script.onload = () => {
          this.waitForUserWayInit()
            .then(() => {
              this.isLoaded = true;
              this.isLoading = false;
              resolve();
            })
            .catch(reject);
        };

        // Handle load error
        script.onerror = () => {
          this.isLoading = false;
          this.loadPromise = null;
          reject(new Error('Failed to load UserWay script'));
        };

        // Append script to head
        document.head.appendChild(script);
      } catch (error) {
        this.isLoading = false;
        this.loadPromise = null;
        reject(error);
      }
    });

    return this.loadPromise;
  }

  /**
   * Wait for UserWay to be fully initialized
   * @returns {Promise<void>} Promise that resolves when UserWay is ready
   */
  waitForUserWayInit() {
    return new Promise((resolve, reject) => {
      const maxAttempts = 50; // 5 seconds max wait time
      let attempts = 0;

      const checkUserWay = () => {
        attempts++;

        // Check if UserWay is available and initialized
        if (window.UserWay && typeof window.UserWay.widgetOpen === 'function') {
          resolve();
          return;
        }

        // Give up after max attempts
        if (attempts >= maxAttempts) {
          reject(new Error('UserWay failed to initialize within timeout'));
          return;
        }

        // Check again in 100ms
        setTimeout(checkUserWay, 100);
      };

      checkUserWay();
    });
  }

  /**
   * Load UserWay and open the widget
   * @param {HTMLElement} triggerElement - The element that triggered the loading (optional)
   * @returns {Promise<void>} Promise that resolves when widget is opened
   */
  async loadAndOpen(triggerElement = null) {
    try {
      // Show loading state on trigger element
      if (triggerElement) {
        this.setElementLoadingState(triggerElement, true);
      }

      await this.load();

      if (window.UserWay && typeof window.UserWay.widgetOpen === 'function') {
        window.UserWay.widgetOpen();

        // Remove loading state
        if (triggerElement) {
          this.setElementLoadingState(triggerElement, false);
        }
      } else {
        throw new Error('UserWay is not properly initialized');
      }
    } catch (error) {
      console.error('Failed to load or open UserWay widget:', error);

      // Remove loading state
      if (triggerElement) {
        this.setElementLoadingState(triggerElement, false);
      }

      // Optional: Show fallback message to user
      this.showFallbackMessage();
    }
  }

  /**
   * Set loading state on an element
   * @param {HTMLElement} element - Element to update
   * @param {boolean} isLoading - Whether element is in loading state
   */
  setElementLoadingState(element, isLoading) {
    if (!element) return;

    if (isLoading) {
      element.style.opacity = '0.6';
      element.style.pointerEvents = 'none';
      element.setAttribute('aria-busy', 'true');

      // Store original text if not already stored
      if (!element.dataset.originalText) {
        element.dataset.originalText = element.textContent;
      }
      element.textContent = 'Loading accessibility widget...';
    } else {
      element.style.opacity = '';
      element.style.pointerEvents = '';
      element.removeAttribute('aria-busy');

      // Restore original text
      if (element.dataset.originalText) {
        element.textContent = element.dataset.originalText;
        delete element.dataset.originalText;
      }
    }
  }

  /**
   * Show a fallback message if UserWay fails to load
   */
  showFallbackMessage() {
    const message =
      'Accessibility widget is temporarily unavailable. Please contact us directly for assistance.';

    // Try to show a user-friendly notification
    if (window.alert) {
      alert(message);
    } else {
      console.warn(message);
    }
  }

  /**
   * Get loading status
   * @returns {Object} Object containing loading and loaded states
   */
  getStatus() {
    return {
      isLoading: this.isLoading,
      isLoaded: this.isLoaded,
      isAvailable:
        this.isLoaded && window.UserWay && typeof window.UserWay.widgetOpen === 'function',
    };
  }
}

// Create global instance
window.UserWayLoader = window.UserWayLoader || new UserWayLoader();

// Export for potential module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = UserWayLoader;
}
